import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import {
  LucideAngularModule,
  Trash2,
  Plus,
  CheckCircle,
  Check,
  ChevronDown,
  ChevronUp,
  MoreVertical,
} from 'lucide-angular';
import { QuestionType } from '../../../../services/assignment.models';
import { QuestionEditService } from '../question-edit.service';

@Component({
  selector: 'app-question-edit-content',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, OverlayModule],
  templateUrl: './question-edit-content.html',
  styleUrl: './question-edit-content.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionEditContentComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly service = inject(QuestionEditService);

  protected readonly QuestionType = QuestionType;

  protected readonly icons = {
    Trash2,
    Plus,
    CheckCircle,
    Check,
    ChevronDown,
    ChevronUp,
    MoreVertical,
  };

  private readonly OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  protected readonly menuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected optionMenuOpenIdx: number | null = null;

  constructor() {
    this.service.stateChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  // ============================================
  // HELPERS
  // ============================================

  protected getOptionLetter(index: number): string {
    return this.OPTION_LETTERS[index] ?? String(index + 1);
  }

  // ============================================
  // OPTION MENU (UI-only state)
  // ============================================

  protected toggleOptionMenu(index: number): void {
    this.optionMenuOpenIdx = this.optionMenuOpenIdx === index ? null : index;
    this.cdr.markForCheck();
  }

  protected closeOptionMenu(): void {
    if (this.optionMenuOpenIdx !== null) {
      this.optionMenuOpenIdx = null;
      this.cdr.markForCheck();
    }
  }

  // ============================================
  // OPTION ACTIONS (delegate to service, close menu)
  // ============================================

  protected toggleOptionCorrect(optionIndex: number): void {
    this.service.toggleOptionCorrect(optionIndex);
    this.cdr.markForCheck();
  }

  protected moveEditOption(index: number, direction: 'up' | 'down'): void {
    this.service.moveOption(index, direction);
    this.optionMenuOpenIdx = null;
    this.cdr.markForCheck();
  }

  protected deleteEditOption(index: number): void {
    this.service.deleteOption(index);
    this.optionMenuOpenIdx = null;
    this.cdr.markForCheck();
  }

  protected addEditOption(): void {
    this.service.addOption();
    this.cdr.markForCheck();
  }
}
